import { Component, forwardRef, Input, OnInit, OnDestroy } from '@angular/core';
import { NG_VALUE_ACCESSOR, ControlValueAccessor } from '@angular/forms';
import { DropdownOptionModel } from '../../models/dropdown-option.model';
import { timeZones, momentTimeZoneMap, agilityTimeZoneMap } from '../../data/time-zones';
import { TimeZoneDetailsModel } from '../../models/time-zone-details.model';
import { CurrentUserStateService } from '../../../../Core/Users/services/current-user-state.service';
import { Subscription, filter } from 'rxjs';
import { LocalizationService } from '../../../../Core/Localization/services/localization.service';

const timezonePickerValueAccessor: any = {
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => TimezonePickerComponent),
    multi: true
};

@Component({
    selector: 'agpr-timezone-picker',
    templateUrl: './timezone-picker.component.html',
    styleUrls: ['./timezone-picker.component.scss'],
    providers: [timezonePickerValueAccessor],
    standalone: false
})
export class TimezonePickerComponent implements ControlValueAccessor, OnInit, OnDestroy {
    private subscriptions: Subscription[] = [];

    @Input() id: string = null;
    @Input() placeholder: string = 'timezone';
    @Input() styleClass: string = '';
    @Input() disabled: boolean = false;
    @Input() defaultToUserPreference: boolean = true;
    @Input() label: string;

    value: TimeZoneDetailsModel;
    timezones: DropdownOptionModel[] = null;
    dropDownOption: DropdownOptionModel = new DropdownOptionModel();

    private defaultZone: TimeZoneDetailsModel;
    defaultSet: boolean = false;

    constructor(
        public userStateService: CurrentUserStateService,
        public localizationService: LocalizationService
    ) {
        this.timezones = this.refreshOptions();
    }

    ngOnInit(): void {
        this.subscriptions.push(
            this.userStateService.getPreferences().pipe(filter(x => !!x)).subscribe((preferences) => {
                if (preferences && preferences.timeZone) {
                    this.defaultZone = momentTimeZoneMap.get(preferences.timeZone);
                    if (!this.defaultZone) {
                        this.defaultZone = agilityTimeZoneMap.get(preferences.timeZone);
                    }
                    this.defaultSet = true;
                    if (this.value === null && this.defaultToUserPreference) {
                        this.value = this.defaultZone;
                        this.dropDownOption = this.timezones.find(option => option.value === this.value);
                        this.timeZoneSelected(this.value);
                    }
                }
            })
        );
        this.subscriptions.push(
            this.localizationService.getLocalizationMapChange().subscribe(() => {
                this.refreshOptions();
            })
        );
    }
    ngOnDestroy(): void {
        this.subscriptions.forEach((value) => {
            value.unsubscribe();
        });
        this.subscriptions = [];
    }

    /**
     * Method required by the ControlValueAccessor
     * to identify when the model has changed
     */
    onModelChange: Function = () => {};

    /**
     * Method required by the ControlValueAccessor
     * to identify when the input has been touched
     */
    onModelTouched: Function = () => {};

    /**
     * Required as part of ControlValueAccessor
     * Adds a new value to the the expression passed to the
     * ngModel property on the component
     */
    writeValue(value: any): void {
        this.value = null;
        if (value) {
            this.value = momentTimeZoneMap.get(value);
            if (!this.value) {
                this.value = agilityTimeZoneMap.get(value);
            }
        }
        if (!this.value && this.defaultZone && this.defaultToUserPreference) {
            this.value = this.defaultZone;
            this.timeZoneSelected(this.value);
        }
        if (!this.value && this.defaultSet) {
            this.value = momentTimeZoneMap.get('UTC');
        }
        this.dropDownOption = this.timezones.find(option => option.value === this.value);
    }

    /**
     * Required as part of ControlValueAccessor
     * sets up a way to know when the model has changed
     */
    registerOnChange(fn: Function): void {
        this.onModelChange = fn;
    }

    /**
     * Required as part of ControlValueAccessor
     * sets up a way to know when the input has been touched
     */
    registerOnTouched(fn: Function): void {
        this.onModelTouched = fn;
    }

    timeZoneSelected(zone: TimeZoneDetailsModel): void {
        if (!zone) {
            this.onModelChange(null);
            return;
        }
        this.onModelChange(zone.momentId);
    }

    /**
     * Gets the list of time zones formatted as dropdown options
     */
    private refreshOptions(): DropdownOptionModel[] {
        const timeZoneOptions: DropdownOptionModel[] = timeZones.map((zone) => {
            const localeOptions: any = {
                gmtOffset: '',
                name: this.localizationService.transform(zone.name)
            };
            const localizationKey = 'timeZoneOption';

            if (zone.gmtOffset !== 0) {
                let hours: number|string = Math.trunc(zone.gmtOffset);
                if (hours >= 0) {
                    hours = '+' + hours;
                }

                let minutes: number|string = Math.abs(zone.gmtOffset) % 1;
                minutes = Math.round(minutes * 60);
                if (minutes < 10) {
                    minutes = '0' + minutes;
                }

                localeOptions.gmtOffset = `${hours}:${minutes}`;
            }

            return {
                label: this.localizationService.transform(
                    localizationKey,
                    localeOptions
                ),
                value: zone
            };
        });

        return timeZoneOptions;
    }
}
